import { apiClient } from 'lib/api';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import type { SynchronousQueryConfig } from 'lib/react-query';
import queryKeys from './queryKeys';

export interface AdSettingsRequestBody {
  app: string;
  platform: string;
  ad_sets: { account_id: string; name: string; id: string; campaign_id: string; }[];
}

export interface AdSettingPageEntry {
  name: string;
  is_default: boolean;
  id: number;
}

export interface AdSettingTextEntry {
  name: string;
  is_default: boolean;
  id: number;
}

export interface AdSettingEntry {
  pages: AdSettingPageEntry[];
  texts: AdSettingTextEntry[];
}

export interface AdSettingEntryWithName extends AdSettingEntry {
  name: string;
}

type AdSettingsResponse = Record<string, AdSettingEntry>;

const getAdSettings = async (body: AdSettingsRequestBody): Promise<AdSettingsResponse> => {
  const res = await apiClient.post<AdSettingsResponse>(
    `/api/v1/facebook_manager/get_ad_settings/`,
    body,
  );

  if (isAxiosError(res)) {
    const error = new Error('Get Ad Settings Failed');
    error.cause = res.response?.data;
    return Promise.reject(error);
  }
  return res.data;
};

interface UseAdSettings<TData> {
  config?: SynchronousQueryConfig<typeof getAdSettings, TData>;
  params: AdSettingsRequestBody;
}

const useAdSettings = <TData = AdSettingsResponse>({ params: body, config }: UseAdSettings<TData>) => {
  return useQuery({
    ...config,
    staleTime: 0,
    refetchOnMount: true,
    queryFn: () => getAdSettings(body),
    queryKey: [
      ...queryKeys.adSettings,
      ...Object.values(body).join(','),
    ],
    retry: false,
  });
};

export default useAdSettings;